import React, { useMemo, useState } from 'react';
import { History, Droplets, Hand, CalendarClock, Filter } from 'lucide-react';
import { useIrrigation } from '../contexts/IrrigationContext';

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const IrrigationHistory: React.FC = () => {
  const { history, zones } = useIrrigation();
  const [zoneFilter, setZoneFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');

  const filtered = useMemo(
    () =>
      history.filter((h) => {
        const zoneMatch = zoneFilter === 'all' || h.zone_id === zoneFilter;
        const kindMatch = kindFilter === 'all' || h.kind === kindFilter;
        return zoneMatch && kindMatch;
      }),
    [history, zoneFilter, kindFilter]
  );

  const totalWater = filtered.reduce((s, h) => s + h.water_liters, 0);
  const totalMinutes = filtered.reduce((s, h) => s + h.duration_minutes, 0);
  const manualCount = filtered.filter((h) => h.kind === 'manual').length;

  const stats = [
    { label: 'Arrosages', value: filtered.length, color: 'var(--color-primary)', icon: History },
    { label: 'Eau totale', value: `${totalWater.toLocaleString('fr-FR')} L`, color: 'var(--color-info)', icon: Droplets },
    { label: 'Durée cumulée', value: `${Math.round(totalMinutes / 60)} h`, color: 'var(--color-success)', icon: CalendarClock },
    { label: 'Lancements manuels', value: manualCount, color: 'var(--color-accent)', icon: Hand },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-heading-2 text-[var(--text-primary)]">Historique d&apos;irrigation</h1>
        <p className="text-body text-[var(--text-secondary)] mt-1">
          Journal détaillé des arrosages programmés et manuels par zone
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <div key={i} className="card text-center" data-testid={`history-stat-${i}`}>
            <s.icon className="w-6 h-6 mx-auto mb-2" style={{ color: s.color }} />
            <div className="text-2xl font-bold mb-1" style={{ color: s.color }}>
              {s.value}
            </div>
            <div className="text-sm text-[var(--text-secondary)]">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-[var(--text-secondary)]">
          <Filter className="w-4 h-4" />
          <span className="text-sm font-medium">Filtres :</span>
        </div>
        <select
          value={zoneFilter}
          onChange={(e) => setZoneFilter(e.target.value)}
          className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
          data-testid="history-zone-filter"
        >
          <option value="all">Toutes les zones</option>
          {zones.map((z) => (
            <option key={z.id} value={z.id}>
              {z.name}
            </option>
          ))}
        </select>
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
          data-testid="history-kind-filter"
        >
          <option value="all">Tous les types</option>
          <option value="scheduled">Programmé</option>
          <option value="manual">Manuel</option>
        </select>
      </div>

      {/* Table */}
      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="history-table">
          <thead>
            <tr className="border-b border-[var(--border-light)] text-left text-[var(--text-secondary)]">
              <th className="px-4 py-3 font-medium">Zone</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Origine</th>
              <th className="px-4 py-3 font-medium">Début</th>
              <th className="px-4 py-3 font-medium">Fin</th>
              <th className="px-4 py-3 font-medium">Durée</th>
              <th className="px-4 py-3 font-medium">Eau</th>
              <th className="px-4 py-3 font-medium">Statut</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((h) => (
              <tr
                key={h.id}
                className="border-b border-[var(--border-light)] last:border-0 hover:bg-[var(--gray-50)]"
                data-testid={`history-row-${h.id}`}
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-[var(--text-primary)]">{h.zone_name}</div>
                  <div className="text-xs text-[var(--text-tertiary)]">{h.field_name}</div>
                </td>
                <td className="px-4 py-3 capitalize text-[var(--text-secondary)]">{h.type}</td>
                <td className="px-4 py-3">
                  {h.kind === 'manual' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md bg-[var(--color-accent)]/15 text-[var(--color-accent)]">
                      <Hand className="w-3 h-3" /> Manuel
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md bg-[var(--color-primary)]/15 text-[var(--color-primary)]">
                      <CalendarClock className="w-3 h-3" /> {h.program_name || 'Programmé'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-[var(--text-secondary)]">{fmtDate(h.started_at)}</td>
                <td className="px-4 py-3 text-[var(--text-secondary)]">{fmtDate(h.ended_at)}</td>
                <td className="px-4 py-3 text-[var(--text-primary)] font-medium">{h.duration_minutes} min</td>
                <td className="px-4 py-3 text-[var(--color-info)] font-medium">{h.water_liters} L</td>
                <td className="px-4 py-3">
                  {h.status === 'completed' ? (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-[var(--color-success)]/15 text-[var(--color-success)]">
                      Terminé
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-[var(--color-warning)]/15 text-[var(--color-warning)]">
                      Arrêté
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-12" data-testid="history-empty">
            <History className="w-12 h-12 text-[var(--text-secondary)] mx-auto mb-4" />
            <p className="text-body text-[var(--text-secondary)]">Aucun arrosage enregistré pour ces filtres</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IrrigationHistory;
